// src/components/issues/BoutonSuivre.tsx — SCE Issues
// Cloche « Suivre » en tête de fiche (Lot 12).
//
// Suivi MANUEL uniquement (décision du 25/09) : on suit parce qu'on a cliqué,
// jamais parce qu'on a déclaré ou commenté. Ouvert à tous les internes,
// opérateurs compris ; pas aux fournisseurs (le B2B n'est pas ouvert, et
// ensureUser ne sait pas encore les résoudre).
//
// Le bouton dit l'état (« Suivre » / « Suivi ») et combien de personnes
// suivent ; au survol, qui. Les notifications aux abonnés sont envoyées par
// Power Automate, selon le canal choisi par chacun (Mes notifications).
//
// Bascule de rôle (Lot 7) : l'état de la personne AFFICHÉE, en lecture seule —
// s'abonner à sa place n'aurait pas de sens.

import { useEffect, useState } from 'react'
import { suivreIssue } from '@/api/issues.api'
import { BoutonCloche } from '@/components/ui/BoutonCloche'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useIdSharePointResolu } from '@/hooks/useIdSharePoint'
import { estAbonne, libelleSuivi } from '@/utils/abonnements'
import type { Issue } from '@/types/issue.types'

export function BoutonSuivre({
  issue,
  itemId,
  onEcrit,
}: {
  issue: Issue
  itemId: string
  /** Appelé après une écriture réussie : la fiche se relit (noms, compte). */
  onEcrit: () => void
}): JSX.Element | null {
  const { user, isFournisseur, enVueRole } = useCurrentUser()
  const snackbar = useSnackbar()
  const { id: monId, resolu } = useIdSharePointResolu(user?.upn)
  const [enCours, setEnCours] = useState(false)
  // La cloche bascule dès que l'écriture a réussi, comme celles du projet et de
  // l'équipement, sans attendre que la fiche se relise. Effacé quand la fiche
  // revient avec les abonnés à jour.
  const [apres, setApres] = useState<boolean | null>(null)
  useEffect(() => setApres(null), [issue.abonnes])

  if (!user || isFournisseur) return null

  const reel = estAbonne(issue.abonnes, monId, user.upn)
  const suis = apres ?? reel
  // Pas encore de quoi savoir si l'on est abonné (identifiant non résolu, adresse
  // absente de la colonne) : même état « en attente » que projet et équipement.
  const indisponible = !resolu && !reel && apres === null
  const total = issue.abonnes.length + (suis === reel ? 0 : suis ? 1 : -1)
  const libelle = libelleSuivi(total, suis)
  const noms = issue.abonnes.map((a) => a.nom).filter(Boolean).join(', ')

  async function basculer(): Promise<void> {
    if (!user || enVueRole || enCours) return
    setEnCours(true)
    try {
      await suivreIssue(itemId, user.upn, !suis)
      setApres(!suis)
      snackbar.show(
        suis ? 'Vous ne suivez plus ce problème.' : 'Vous suivez ce problème : il apparaît sur votre accueil.',
        'success',
      )
      onEcrit()
    } catch (err: unknown) {
      snackbar.show(err instanceof Error ? err.message : 'Abonnement impossible.', 'error')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <BoutonCloche
      suis={suis}
      total={total}
      enCours={enCours}
      indisponible={indisponible}
      lectureSeule={enVueRole}
      ariaLabel={`${suis ? 'Ne plus suivre ce problème' : 'Suivre ce problème'} — ${libelle}`}
      titre={enVueRole ? `${libelle} (lecture seule en vue d’un autre profil)` : noms ? `${libelle} : ${noms}` : libelle}
      onClick={() => void basculer()}
    />
  )
}
